import React, { useEffect, useRef, useState } from "react";
import {
  createDonationPurpose,
  deleteDonationPurpose,
  getDonationPurposes,
  updateDonationPurpose,
} from "../../api/donationsApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const emptyForm = () => ({
  id: null,
  purpose_name: "",
  description: "",
  status_id: 1,
});

export default function DonationPurposesMaster({ onDirectoryChange }) {
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const alertRef = useRef(null);

  const ALERT_ID = "donation-purpose-master-alert";

  const loadPurposes = async () => {
    try {
      setLoading(true);
      const response = await getDonationPurposes({ scope: "master" });
      if (response && response.success) setPurposes(response.data || []);
      else setPurposes(response || []);
    } catch (err) {
      setError(err.message || "Failed to load donation purposes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurposes();
  }, []);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    const wasEdit = isEditing;

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      if (wasEdit) {
        await updateDonationPurpose(
          formData.id,
          {
            purpose_name: formData.purpose_name.trim(),
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        await createDonationPurpose(
          {
            purpose_name: formData.purpose_name.trim(),
            description: formData.description,
          },
          csrfToken,
        );
      }

      setFormData(emptyForm());
      setIsEditing(false);
      setSuccessMsg(
        wasEdit
          ? "Donation purpose updated successfully."
          : "Donation purpose added successfully.",
      );
      await loadPurposes();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message || "Failed to save donation purpose");
    }
  };

  const handleEdit = (purpose) => {
    setSuccessMsg("");
    setFormData({
      id: purpose.id,
      purpose_name: purpose.purpose_name || "",
      description: purpose.description || "",
      status_id:
        purpose.status_id != null && purpose.status_id !== ""
          ? Number(purpose.status_id)
          : 1,
    });
    setIsEditing(true);
    setFormError(null);
    setFormErrorDetails({});
  };

  const handleCancel = () => {
    setFormData(emptyForm());
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteDonationPurpose(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Donation purpose removed from the directory.");
      await loadPurposes();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <p className="ub-loading">Loading donation purposes…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(purposes) ? purposes : purposes.data || [];

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />

      <h3 className="ub-section__subhead" style={{ marginBottom: "1rem" }}>
        Donation purpose master
      </h3>

      <GlobalFormShell
        legend={isEditing ? "Edit donation purpose" : "Add donation purpose"}
        showAlert={!!formError}
        alertText={formError}
        alertId={ALERT_ID}
        formAlertRef={alertRef}
        actions={
          <>
            {isEditing ? (
              <GlobalButton variant="ghost" onClick={handleCancel}>
                Cancel edit
              </GlobalButton>
            ) : null}
            <GlobalButton variant="primary" onClick={handleSubmit}>
              {isEditing ? "Save changes" : "Create purpose"}
            </GlobalButton>
          </>
        }
      >
        <GlobalTextField
          id={isEditing ? "donation_purpose_name_edit" : "donation_purpose_name"}
          label="Purpose name"
          value={formData.purpose_name}
          onChange={(v) => handleInputChange("purpose_name", v)}
          error={formErrorDetails.purpose_name}
          baseAlertId={ALERT_ID}
          required
          placeholder="e.g. Temple renovation"
        />
        <GlobalTextField
          id={
            isEditing
              ? "donation_purpose_description_edit"
              : "donation_purpose_description"
          }
          label="Description"
          value={formData.description}
          onChange={(v) => handleInputChange("description", v)}
          error={formErrorDetails.description}
          baseAlertId={ALERT_ID}
          placeholder="Optional details for reports and filters"
        />
        {isEditing ? (
          <GlobalSelectField
            id="donation_purpose_status_id"
            label="Status"
            value={String(formData.status_id)}
            onChange={(v) => handleInputChange("status_id", Number(v))}
            options={STATUS_OPTIONS}
            baseAlertId={ALERT_ID}
            placeholderOption="Select active or inactive…"
          />
        ) : null}
      </GlobalFormShell>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Donation purposes master table"
        headers={[
          { key: "purpose_name", label: "Purpose name" },
          { key: "description", label: "Description" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Created date" },
          { key: "actions", label: "Actions", className: "ub-table__col-actions" },
        ]}
      >
        {dataList.map((purpose) => (
          <tr key={purpose.id}>
            <td style={{ fontWeight: "500" }}>{purpose.purpose_name}</td>
            <td>{purpose.description || "—"}</td>
            <td>
              <GlobalBadge
                active={
                  purpose.status_id == null || Number(purpose.status_id) === 1
                }
              />
            </td>
            <td>
              {purpose.created_at
                ? new Date(purpose.created_at).toLocaleDateString()
                : "—"}
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">
                <GlobalTableActionButton action="edit" onClick={() => handleEdit(purpose)} />
                <GlobalTableActionButton
                  action="delete"
                  onClick={() => {
                    setItemToDelete(purpose);
                    setShowDeleteConfirm(true);
                  }}
                />
              </div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={5}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No donation purposes found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove donation purpose <b>{itemToDelete?.purpose_name}</b> from the
          directory?
        </p>
      </GlobalModal>
    </div>
  );
}

